<template>
  <div class="text-xs-center">
    <highcharts
      v-if="dataLoaded"
      ref="wrapper"
      class="stock"
      :constructor-type="'stockChart'"
      :options="stockOptions"
    ></highcharts>
    <div v-else>
      <v-progress-circular
        :size="50"
        color="primary"
        indeterminate
      ></v-progress-circular>
      <h2>{{ status }}</h2>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range";

export default {
  props: {
    market: null,
    sellOrders: null,
    buyOrders: null,
    closedSellOrders: null,
    closedBuyOrders: null
  },
  data: function() {
    const intialDarkMode = window.localStorage.getItem("nightmode") === "true";
    const vm = this;
    const timezone = moment()
      .tz(moment.tz.guess())
      .format("z");

    return {
      lastMin: 1, // instead of 0, prevents devision by 0 errors.
      lastMax: 1,
      dataLoaded: false,
      enriched: false,
      status: "Loading candlesticks...",
      stockOptions: {
        yAxis: {
          floor: 0,
          plotLines: [],
          scrollbar: {
            enabled: true,
            showFull: false
          }
        },

        tooltip: {
          headerFormat: `{point.x:%Y-%m-%d %H:%M:%S} ${timezone}`
        },

        xAxis: {
          events: {
            afterSetExtremes: _.debounce(this.enrichData, 250)
          },
          minRange: 3600 * 1000,
          title: {
            text: timezone,
            style: {
              color: this.darkmode ? "white" : "#333"
            }
          }
        },

        navigator: {
          series: {
            color: "#333",
            lineWidth: 2
          },
          adaptToUpdatedData: false
        },

        scrollbar: {
          liveRedraw: false
        },

        rangeSelector: {
          buttons: [
            {
              type: "hour",
              count: 1,
              text: "1h"
            },
            {
              type: "day",
              count: 1,
              text: "1d"
            },
            {
              type: "week",
              count: 1,
              text: "1w"
            },
            {
              type: "all",
              count: 1,
              text: "All"
            }
          ],
          selected: 1,
          inputEnabled: false
        },

        plotOptions: {
          candlestick: {
            color: this.$vuetify.theme.error,
            upColor: this.$vuetify.theme.success
          },
          series: {
            turboThreshold: 1500
          }
        },

        chart: {
          zoomType: "xy",
          backgroundColor: intialDarkMode ? "#212121" : "#fff",
          height: "500px",
          marginRight: 67,
          events: {
            load: function(e) {
              if (!vm.enriched) {
                // vm.$refs.wrapper.chart.redraw();
                e.target.redraw();
              }
            }
          }
        },

        loading: {
          style: {
            position: "absolute",
            backgroundColor: intialDarkMode ? "#212121" : "#ffffff",
            opacity: 0.5,
            textAlign: "center",
            color: intialDarkMode ? "#fff" : "#333333"
          }
        },

        title: {
          style: {
            color: intialDarkMode ? "#E9E9E9" : "#103A84",
            fontSize: "18px",
            fontFamily: "Lato, serif",
            lineHeight: "22px",
            fontWeight: "normal"
          }
        },

        exporting: {
          buttons: {
            contextButton: {
              symbol: intialDarkMode
                ? "url(/img/highcharts_menu_dark.svg)"
                : "url(/img/highcharts_menu_light.svg)",
              width: 32,
              height: 32,
              theme: {
                fill: intialDarkMode ? "#212121" : "#ffffff"
              }
            }
          }
        },

        series: [
          {
            name: "",
            type: "candlestick",
            id: "candlestick",
            data: [],
            tooltip: {
              valueDecimals: 8
            },
            dataGrouping: {
              enabled: true,
              groupPixelWidth: 8
            }
          },
          {
            type: "scatter",
            data: [],
            color: "orange",
            tooltip: {
              headerFormat: "",
              pointFormat: `Sold {point.quantity} at {point.y} on {point.x:%Y-%m-%d %H:%M:%S} ${timezone}`
            },
            marker: {
              symbol: "triangle-down",
              radius: 3
            }
          },
          {
            type: "scatter",
            data: [],
            color: "#00ff00",
            tooltip: {
              headerFormat: "",
              pointFormat: `Bought {point.quantity} at {point.y} on {point.x:%Y-%m-%d %H:%M:%S} ${timezone}`
            },
            marker: {
              symbol: "triangle",
              radius: 3
            }
          }
        ]
      }
    };
  },

  computed: {
    ...mapGetters({
      darkmode: "settings/darkmode"
    })
  },

  watch: {
    market: function() {
      this.loadData();
    },
    darkmode: function(darkMode) {
      this.stockOptions.title.style.color = darkMode ? "#E9E9E9" : "#103A84";
      this.stockOptions.exporting.buttons.contextButton.symbol = darkMode
        ? "url(/img/highcharts_menu_dark.svg)"
        : "url(/img/highcharts_menu_light.svg)";

      this.stockOptions.exporting.buttons.contextButton.theme.fill = darkMode
        ? "#212121"
        : "#ffffff";

      this.stockOptions.chart.backgroundColor = darkMode ? "#212121" : "#fff";
      this.stockOptions.title.color = darkMode ? "#ffffff" : "#333333";

      this.stockOptions.loading.style.backgroundColor = darkMode
        ? "#212121"
        : "#fff";
      this.stockOptions.loading.style.color = darkMode ? "#fff" : "#333333";

      this.$refs.wrapper.chart.redraw();
    }
  },

  created: function() {
    this.loadData();
  },

  methods: {
    enrichData: async function(e) {
      let min = Math.round(e.min);
      let max = Math.round(e.max);

      if (isNaN(min) || isNaN(max)) {
        return;
      }

      if (
        Math.abs(min / this.lastMin - 1) <= 0.002 &&
        Math.abs(max / this.lastMax - 1) <= 0.002
      ) {
        // chart isn't reloaded because the difference is less than 0.2%
        return;
      }

      this.lastMin = min;
      this.lastMax = max;

      this.$refs.wrapper.chart.showLoading(this.status);
      const url = `/api/v1/chart/candles/${this.market}/${min}/${max}/1`;
      await axios.get(url).then(res => {
        const data = this.mergeEnrichedData(
          this.stockOptions.series[0].data,
          res.data
        );

        this.$refs.wrapper.chart.hideLoading();
        this.$refs.wrapper.chart.series[0].setData(data);

        this.enriched = true;
      });
    },
    mergeEnrichedData: function(currentData, newData) {
      let merged = [...currentData, ...newData];
      let structured = {};
      for (let item of merged) {
        structured[item[0]] = item;
      }

      return Object.values(structured).sort((a, b) => {
        if (a[0] === b[0]) {
          return 0;
        }

        return a[0] < b[0] ? -1 : 1;
      });
    },
    loadData: async function(e) {
      try {
        this.dataLoaded = false;
        this.enriched = false;
        this.lastMin = 1;
        this.lastMax = 1;

        await axios.get(`/api/v1/chart/candles/${this.market}`).then(res => {
          this.stockOptions.series[0].data = res.data;
          this.stockOptions.navigator.data = res.data;
          this.stockOptions.title.text = this.market;
        });

        this.stockOptions.yAxis.plotLines = [];
        this.sellOrders &&
          (await this.sellOrders.map(sellPrice => {
            this.stockOptions.yAxis.plotLines.push({
              value: sellPrice,
              label: {
                text: sellPrice.toFixed(8).substring(0, 9),
                align: "right",
                x: 65,
                y: 4,
                style: {
                  color: "white",
                  backgroundColor: this.$vuetify.theme.error,
                  fontWeight: "bold"
                },
                useHTML: true
              },
              color: this.$vuetify.theme.error,
              dashStyle: "solid",
              width: 0.5
            });
          }));

        this.buyOrders &&
          (await this.buyOrders.map(buyPrice => {
            this.stockOptions.yAxis.plotLines.push({
              value: buyPrice,
              label: {
                text: buyPrice.toFixed(8).substring(0, 9),
                align: "right",
                x: 65,
                y: 4,
                style: {
                  color: "white",
                  backgroundColor: this.$vuetify.theme.success,
                  fontWeight: "bold"
                },
                useHTML: true
              },
              color: this.$vuetify.theme.success,
              dashStyle: "solid",
              width: 0.5
            });
          }));

        this.stockOptions.series[1].data = [];
        this.closedSellOrders &&
          (await this.closedSellOrders.map(order => {
            this.stockOptions.series[1].data.push({
              x: order.timestamp,
              y: order.price,
              quantity: order.quantity
            });
          }));

        this.stockOptions.series[2].data = [];
        this.closedBuyOrders &&
          (await this.closedBuyOrders.map(order => {
            this.stockOptions.series[2].data.push({
              x: order.timestamp,
              y: order.price,
              quantity: order.quantity
            });
          }));

        this.dataLoaded = true;
      } catch (e) {
        console.log(e);
      }
    }
  }
};
</script>
<style scoped>
.stock {
  width: 100%;
  margin: 0 auto;
}
.highcharts-button-box {
  width: 32px;
  height: 32px;
}
>>> .highcharts-button-symbol {
  width: 32px;
  height: 32px;
  transform: translate(-6, -4);
}
>>> .theme--light .highcharts-button-hover .highcharts-button-box,
>>> .theme--light .highcharts-button-pressed .highcharts-button-box {
  fill: #ffffff;
}
>>> .theme--dark .highcharts-button-hover .highcharts-button-box,
>>> .theme--dark .highcharts-button-pressed .highcharts-button-box {
  fill: #212121;
}
</style>



// WEBPACK FOOTER //
// resources/js/components/openorders/StockChart.vue